import { useState } from "react";

const DEFAULT_ENVIRONMENTS = [
  "Office-based",
  "Remote",
  "Hybrid",
  "Field Work",
  "Flexible Schedule",
];

export default function WorkPreferenceSelector({ form, updateField }) {
  const [adding, setAdding] = useState(false);
  const [custom, setCustom] = useState("");
  const [message, setMessage] = useState("");

  const selected = Array.isArray(form.Work_Environment) ? form.Work_Environment : [];
  const selectedValue = selected[0] || "";

  const isDefaultSelected =
    !!selectedValue && DEFAULT_ENVIRONMENTS.includes(selectedValue);

  const isCustomSelected =
    !!selectedValue && !DEFAULT_ENVIRONMENTS.includes(selectedValue);

  const setMsg = (msg) => {
    setMessage(msg);
    if (msg) {
      setTimeout(() => setMessage(""), 2000);
    }
  };

  const setBoth = (value) => {
    const arr = value ? [value] : [];
    updateField("Work_Environment", arr);
    updateField("Work_Preferences", arr);
  };

  const selectDefault = (value) => {
    setBoth(value);
    setAdding(false);
    setCustom("");
    setMsg("Saved successfully ✅");
  };

  const openCustomAdd = () => {
    if (isDefaultSelected) {
      setMsg("Clear the selected option before adding a custom environment");
      return;
    }

    setAdding(true);
    setCustom("");
    setMessage("");
  };

  const addCustom = () => {
    const v = custom.trim();
    if (!v) {
      setMsg("Please enter a work environment");
      return;
    }

    const duplicateDefault = DEFAULT_ENVIRONMENTS.find(
      (item) => item.toLowerCase() === v.toLowerCase()
    );

    if (duplicateDefault) {
      setMsg("This already exists in the list. Please select it above.");
      return;
    }

    if (isDefaultSelected) {
      setMsg("You can select one option or add one custom environment, not both");
      return;
    }

    setBoth(v);
    setAdding(false);
    setCustom("");
    setMsg("Custom environment added ✅");
  };

  const clearSelection = () => {
    setBoth("");
    setAdding(false);
    setCustom("");
    setMsg("Selection cleared");
  };

  return (
    <div className="rounded-xl border border-pink-400/20 bg-white/5 p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-pink-300 text-sm font-semibold">
          What is your preferred work environment?
        </p>

        <span className="text-xs text-white/60 whitespace-nowrap">
          {selectedValue ? "1 selected" : "0 selected"}
        </span>
      </div>

      <p className="text-xs text-white/60 mt-1 mb-3">
        Select one option from the list or add one custom environment.
      </p>

      <div className="max-h-[40vh] overflow-y-auto pr-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
          {DEFAULT_ENVIRONMENTS.map((e) => (
            <label
              key={e}
              className="min-w-0 flex items-start gap-2 text-[13px] sm:text-sm text-white/85 leading-snug cursor-pointer hover:text-white"
            >
              <input
                type="radio"
                name="work-environment"
                className="mt-1 accent-pink-500 flex-shrink-0"
                checked={selectedValue === e}
                onChange={() => selectDefault(e)}
              />
              <span className="min-w-0 whitespace-normal break-words">{e}</span>
            </label>
          ))}
        </div>
      </div>

      {isCustomSelected && (
        <div className="mt-3 rounded-lg border border-pink-400/30 bg-[#11111a] px-3 py-2">
          <p className="text-xs text-white/70">
            Custom environment added:{" "}
            <span className="text-pink-300 font-medium">{selectedValue}</span>
          </p>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-3">
        {!adding && !isCustomSelected && (
          <button
            type="button"
            className="text-pink-400 underline text-sm"
            onClick={openCustomAdd}
          >
            + Add Preferred Environment
          </button>
        )}

        {selectedValue && (
          <button
            type="button"
            onClick={clearSelection}
            className="text-xs text-red-400 underline"
          >
            Clear selection
          </button>
        )}
      </div>

      {adding && (
        <div className="mt-3">
          <input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            className="w-full px-3 py-2 bg-[#11111a] border border-pink-400/40 rounded text-sm"
            placeholder="Enter one custom work environment"
          />

          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={addCustom}
              className="bg-pink-500 px-3 py-1 rounded text-black text-sm"
            >
              Add
            </button>

            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setCustom("");
                setMessage("");
              }}
              className="bg-gray-600 px-3 py-1 rounded text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {message && (
        <p
          className={`text-sm mt-2 ${
            message.includes("✅") ? "text-green-400" : "text-red-400"
          }`}
        >
          {message}
        </p>
      )}
    </div>
  );
}